// @tennis-matches/services/tournament/hooks – generic tournament setup state (Schleifchen + Clubmeisterschaft)

import { useState, useMemo, useEffect, useRef } from "react";
import {
  __asStr,
  shouldClearSchleifchenPlayoffsMode,
} from "@tennis-matches/core/utils";
import {
  defaultTournamentAudience,
  defaultTournamentVenue,
  normalizeTournamentAudience,
  normalizeTournamentVenue,
} from "../tournamentAudienceVenue.js";
import {
  defaultTournamentRegistration,
  normalizeTournamentRegistration,
} from "../tournamentRegistration.js";
import {
  localYmd,
  normalizeTournamentStartIfPast,
  suggestDefaultStartDateTime,
} from "../tournamentStartDateTime.js";
import {
  defaultTournamentParticipants,
  normalizeTournamentParticipants,
  TOURNAMENT_PARTICIPANTS_LIMITS,
} from "../tournamentParticipants.js";

function __clampInt(x, min, max, fallback) {
  const n = Number(x);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

const LK_MIN = 1;
const LK_MAX = 25;

/** Anzahl nicht-leerer Spielernamen (wie MatchMixer actualPlayerCount aus names). */
function __countFilledNames(names) {
  if (!names || typeof names !== "object") return 0;
  return Object.values(names).filter((name) => __asStr(name).trim().length > 0)
    .length;
}

function __sortPlayersByLk(names, lkByPlayer, excludesByPlayer, manualTeams) {
  const entries = Object.entries(names || {})
    .filter(([, v]) => {
      const n = __asStr(v).trim();
      return n.length > 0 && n !== "(Optional)" && n !== "(optional)";
    })
    .map(([k, v]) => {
      const idx = Number(k);
      const lkRaw = (lkByPlayer || {})[k];
      const lk =
        lkRaw === "" || lkRaw == null
          ? Infinity
          : Number(String(lkRaw).replace(",", "."));
      return {
        idx,
        name: __asStr(v).trim(),
        lk: Number.isFinite(lk) ? lk : Infinity,
      };
    });
  if (entries.length === 0) {
    return {
      newNames: { ...names },
      newLkByPlayer: { ...(lkByPlayer || {}) },
      newExcludesByPlayer: { ...(excludesByPlayer || {}) },
      newManualTeams: [...(manualTeams || [])],
      orderChanged: false,
    };
  }
  const hasLk = entries.some((e) => Number.isFinite(e.lk) && e.lk !== Infinity);
  if (!hasLk) {
    return {
      newNames: { ...names },
      newLkByPlayer: { ...(lkByPlayer || {}) },
      newExcludesByPlayer: { ...(excludesByPlayer || {}) },
      newManualTeams: [...(manualTeams || [])],
      orderChanged: false,
    };
  }
  entries.sort((a, b) => a.lk - b.lk);
  const sortedIndices = entries.map((e) => e.idx);
  const currentIndices = entries
    .slice()
    .sort((a, b) => a.idx - b.idx)
    .map((e) => e.idx);
  const orderChanged =
    sortedIndices.length !== currentIndices.length ||
    sortedIndices.some((v, i) => currentIndices[i] !== v);
  if (!orderChanged) {
    return {
      newNames: { ...names },
      newLkByPlayer: { ...(lkByPlayer || {}) },
      newExcludesByPlayer: { ...(excludesByPlayer || {}) },
      newManualTeams: [...(manualTeams || [])],
      orderChanged: false,
    };
  }
  const oldToNew = {};
  const newNames = {};
  const newLkByPlayer = {};
  entries.forEach((e, i) => {
    const newIdx = i + 1;
    oldToNew[e.idx] = newIdx;
    newNames[String(newIdx)] = e.name;
    if (Number.isFinite(e.lk) && e.lk !== Infinity)
      newLkByPlayer[String(newIdx)] = String(e.lk);
  });
  const newExcludesByPlayer = {};
  for (const [oldKey, arr] of Object.entries(excludesByPlayer || {})) {
    const oldIdx = Number(oldKey);
    const newIdx = oldToNew[oldIdx];
    if (newIdx == null) continue;
    const mapped = (arr || [])
      .map((other) => oldToNew[other])
      .filter((n) => n != null);
    if (mapped.length > 0) newExcludesByPlayer[String(newIdx)] = mapped;
  }
  const newManualTeams = (manualTeams || [])
    .map(([a, b]) => {
      const na = oldToNew[a];
      const nb = oldToNew[b];
      if (na == null || nb == null) return null;
      return [na, nb].sort((x, y) => x - y);
    })
    .filter(Boolean);
  return {
    newNames,
    newLkByPlayer,
    newExcludesByPlayer,
    newManualTeams,
    orderChanged: true,
  };
}

/**
 * Generic tournament setup state hook.
 * @param {Object} state - Application state
 * @param {string} [moduleKey="schleifchen"] - state.modules[moduleKey] for tournament/schedule/resultRules
 * @param {boolean} wantNewSetupFromUrl - Whether user wants new setup (?new=1)
 * @returns {Object} Setup state and setters
 */
export function useTournamentSetup(
  state,
  moduleKey = "schleifchen",
  wantNewSetupFromUrl = false,
) {
  const mod = state?.modules?.[moduleKey] || {};
  const tournament = mod.tournament || {};
  const meta = tournament?.meta || {};
  const storageKey = `ttb:${moduleKey}:setupMode`;
  // Beim Zurückgehen von Schedule: Einstellungen auch aus meta.settings (Loader speichert dort)
  const settings = tournament?.settings || meta?.settings || {};

  const [tournamentName, setTournamentName] = useState(() => {
    if (wantNewSetupFromUrl) return "";
    return __asStr(meta?.name || "");
  });
  const defaultStartClock = moduleKey === "club" ? "09:00" : "18:00";
  const [startDate, setStartDate] = useState(() => {
    if (wantNewSetupFromUrl) {
      return suggestDefaultStartDateTime({ defaultClock: defaultStartClock })
        .startDate;
    }
    const rawDate = __asStr(meta?.startDate || localYmd());
    const rawTime = __asStr(
      meta?.startTime || meta?.startsAt || defaultStartClock,
    );
    return normalizeTournamentStartIfPast(rawDate, rawTime, {
      defaultClock: defaultStartClock,
    }).startDate;
  });
  const [startTime, setStartTime] = useState(() => {
    if (wantNewSetupFromUrl) {
      return suggestDefaultStartDateTime({ defaultClock: defaultStartClock })
        .startTime;
    }
    const rawDate = __asStr(meta?.startDate || localYmd());
    const rawTime = __asStr(
      meta?.startTime || meta?.startsAt || defaultStartClock,
    );
    return normalizeTournamentStartIfPast(rawDate, rawTime, {
      defaultClock: defaultStartClock,
    }).startTime;
  });
  const [format, setFormat] = useState(() => {
    if (wantNewSetupFromUrl)
      return moduleKey === "club" ? "singles" : "doubles";
    return __asStr(
      tournament?.format || (moduleKey === "club" ? "singles" : "doubles"),
    );
  });

  const defaultGroupsKo = () => ({
    groups: {
      groupCount: 4,
      seedingMode: "random",
      seedCount: 0,
      allowUnevenGroups: true,
    },
    qualification: { advancePerGroup: 2, includeBestThirds: 0 },
    ko: { bracketSize: 8, thirdPlaceMatch: false, consolation: "none" },
  });
  const [groupsKo, setGroupsKo] = useState(() => {
    if (wantNewSetupFromUrl) return defaultGroupsKo();
    const gk = tournament?.format?.groupsKo;
    if (!gk || typeof gk !== "object") return defaultGroupsKo();
    return {
      groups: { ...defaultGroupsKo().groups, ...(gk.groups || {}) },
      qualification: {
        ...defaultGroupsKo().qualification,
        ...(gk.qualification || {}),
      },
      ko: { ...defaultGroupsKo().ko, ...(gk.ko || {}) },
    };
  });

  const [tournamentMode, setTournamentMode] = useState(() => {
    if (moduleKey !== "club") return "groupsKo";
    if (wantNewSetupFromUrl) return "groupsKo";
    const m = __asStr(tournament?.format?.mode || "groupsKo");
    return m === "ko" || m === "groupsKo" || m === "swiss" ? m : "groupsKo";
  });

  const defaultKoOnly = () => ({
    ko: {
      bracketSize: 8,
      seedingMode: "random",
      seedCount: 0,
      thirdPlaceMatch: false,
    },
  });
  const [koOnly, setKoOnly] = useState(() => {
    if (moduleKey !== "club") return defaultKoOnly();
    if (wantNewSetupFromUrl) return defaultKoOnly();
    const ko = tournament?.format?.ko;
    if (!ko || typeof ko !== "object") return defaultKoOnly();
    return {
      ko: {
        bracketSize: Number(ko.bracketSize) || 8,
        seedingMode: ko.seedingMode === "seeded" ? "seeded" : "random",
        seedCount: Number(ko.seedCount) || 0,
        thirdPlaceMatch: !!ko.thirdPlaceMatch,
      },
    };
  });

  const defaultMatchRules = () => ({ scoring: "bo3", setEnd: "tb" });
  const [matchRules, setMatchRules] = useState(() => {
    if (moduleKey !== "club") return defaultMatchRules();
    if (wantNewSetupFromUrl) return defaultMatchRules();
    const mr = tournament?.matchRules;
    if (!mr || typeof mr !== "object") return defaultMatchRules();
    const scoring = mr.scoring === "mtb" ? "mtb" : "bo3";
    const setEnd = mr.setEnd === "open" ? "open" : "tb";
    return { scoring, setEnd };
  });

  const defaultPlayersCount = moduleKey === "club" ? 8 : 12;
  const [playersCount, setPlayersCount] = useState(() => {
    if (wantNewSetupFromUrl) return defaultPlayersCount;
    const c =
      state?.shared?.players?.count ??
      mod?.players?.count ??
      defaultPlayersCount;
    return __clampInt(c, 1, 99, defaultPlayersCount);
  });

  const getValidPlayersCount = useMemo(() => {
    if (typeof playersCount === "string" && playersCount.trim() === "")
      return defaultPlayersCount;
    const num = Number(playersCount);
    return isNaN(num) || num < 1 || num > 99 ? defaultPlayersCount : num;
  }, [playersCount, defaultPlayersCount]);

  const [names, setNames] = useState(() => {
    if (wantNewSetupFromUrl && moduleKey === "club") {
      return Object.fromEntries(
        [...Array(8)].map((_, i) => [String(i + 1), ""]),
      );
    }
    if (wantNewSetupFromUrl) return {};
    return { ...(state?.shared?.players?.names || mod?.players?.names || {}) };
  });

  const defaultMinutesPerRound = moduleKey === "club" ? 15 : 20;
  const defaultTotalMinutesFallback = moduleKey === "club" ? 90 : 60;
  const [minutesPerRound, setMinutesPerRound] = useState(() => {
    if (wantNewSetupFromUrl) return defaultMinutesPerRound;
    return __clampInt(
      tournament?.minutesPerRound ?? defaultMinutesPerRound,
      1,
      60,
      defaultMinutesPerRound,
    );
  });
  const [totalMinutes, setTotalMinutes] = useState(() => {
    if (wantNewSetupFromUrl) return defaultTotalMinutesFallback;
    const tm =
      tournament?.totalMinutes ??
      (Number.isFinite(Number(tournament?.rounds)) &&
      Number.isFinite(Number(tournament?.minutesPerRound))
        ? Number(tournament.rounds) * Number(tournament.minutesPerRound)
        : defaultTotalMinutesFallback);
    return __clampInt(tm, 10, 600, defaultTotalMinutesFallback);
  });

  const [courtsCount, setCourtsCount] = useState(() => {
    if (wantNewSetupFromUrl) return 3;
    return __clampInt(
      tournament?.courtsCount ?? tournament?.courts?.count ?? 3,
      1,
      24,
      3,
    );
  });
  const [courtsNumbersRaw, setCourtsNumbersRaw] = useState(() => {
    if (wantNewSetupFromUrl) return "";
    const arr = Array.isArray(tournament?.courts?.numbers)
      ? tournament.courts.numbers
      : [];
    return arr.length ? arr.join(", ") : "";
  });

  const [pauseBetweenRounds, setPauseBetweenRounds] = useState(() => {
    if (wantNewSetupFromUrl) return 0;
    return __clampInt(
      tournament?.pauseBetweenRounds ??
        tournament?.settings?.pauseBetweenRounds ??
        0,
      0,
      30,
      0,
    );
  });
  const [halftimeBreak, setHalftimeBreak] = useState(() => {
    if (wantNewSetupFromUrl) return 0;
    return __clampInt(
      tournament?.halftimeBreak ?? tournament?.settings?.halftimeBreak ?? 0,
      0,
      30,
      0,
    );
  });
  const [warmupMinutes, setWarmupMinutes] = useState(() => {
    if (wantNewSetupFromUrl) return 0;
    return __clampInt(
      tournament?.warmupMinutes ?? tournament?.settings?.warmupMinutes ?? 0,
      0,
      30,
      0,
    );
  });

  const [pointsWin, setPointsWin] = useState(() => {
    if (wantNewSetupFromUrl) return 2;
    return Number(tournament?.points?.win ?? 2);
  });
  const [pointsDraw, setPointsDraw] = useState(() => {
    if (wantNewSetupFromUrl) return 1;
    return Number(tournament?.points?.draw ?? 1);
  });

  const [rrMode, setRrMode] = useState(() => {
    if (wantNewSetupFromUrl) return "ONE_SET_OPEN";
    return __asStr(
      mod?.resultRules?.mode ||
        tournament?.settings?.rrMode ||
        meta?.settings?.rrMode ||
        meta?.rrMode ||
        "ONE_SET_OPEN",
    );
  });
  const [allowBeyond7, setAllowBeyond7] = useState(() => {
    if (wantNewSetupFromUrl) return true;
    return mod?.resultRules?.allowBeyond7 !== undefined
      ? !!mod.resultRules.allowBeyond7
      : true;
  });

  const [expressMode, setExpressMode] = useState(() => {
    if (wantNewSetupFromUrl) return true;
    return settings.expressMode !== false;
  });
  const [alwaysAskForRest, setAlwaysAskForRest] = useState(() => {
    if (wantNewSetupFromUrl) return false;
    return settings.alwaysAskForRest === true;
  });
  const [alwaysAskForChanges, setAlwaysAskForChanges] = useState(() => {
    if (wantNewSetupFromUrl) return false;
    return settings.alwaysAskForChanges === true;
  });

  const [teamAllocationMode, setTeamAllocationMode] = useState(() => {
    if (wantNewSetupFromUrl) return "random";
    const v = __asStr(settings?.teamAllocationMode || "");
    const valid =
      v === "manual" || v === "random" || v === "lkBased" ? v : null;
    const fmt = __asStr(tournament?.format || "doubles");
    if (valid) return valid;
    return fmt === "fixedDoubles" ? "lkBased" : "random";
  });
  const prevFormatRef = useRef(format);
  useEffect(() => {
    if (prevFormatRef.current !== format) {
      prevFormatRef.current = format;
      if (format === "fixedDoubles") setTeamAllocationMode("lkBased");
      else if (format === "doubles") setTeamAllocationMode("random");
    } else if (format === "fixedDoubles" && teamAllocationMode === "random") {
      setTeamAllocationMode("lkBased");
    }
  }, [format, teamAllocationMode]);

  // Wettkampf (ONE_SET_TB): 20 Min pro Runde als Vorschlag im Feintuning
  const prevRrModeRef = useRef(rrMode);
  useEffect(() => {
    if (prevRrModeRef.current !== rrMode && rrMode === "ONE_SET_TB") {
      setMinutesPerRound(20);
    }
    prevRrModeRef.current = rrMode;
  }, [rrMode, setMinutesPerRound]);

  useEffect(() => {
    if (format === "fixedDoubles") {
      const num = Number(playersCount);
      const n = Number.isFinite(num) ? num : defaultPlayersCount;
      if (n % 2 !== 0) setPlayersCount(n + 1);
    }
    // Only react to format change so we don't loop when correcting count
  }, [format]);
  const [manualTeams, setManualTeams] = useState(() => {
    if (wantNewSetupFromUrl) return [];
    const arr = settings?.manualTeams;
    return Array.isArray(arr)
      ? arr
          .map((p) =>
            (Array.isArray(p)
              ? [Number(p[0]), Number(p[1])].filter(Number.isFinite)
              : []
            ).filter((p) => p.length === 2),
          )
          .filter((p) => p.length === 2)
      : [];
  });
  const [lkByPlayer, setLkByPlayer] = useState(() => {
    if (wantNewSetupFromUrl) return {};
    const o = settings?.lkByPlayer;
    if (!o || typeof o !== "object") return {};
    const out = {};
    for (const [k, v] of Object.entries(o)) {
      const n = Number(String(v).replace(",", "."));
      if (Number.isFinite(n)) {
        out[String(k)] = String(Math.max(LK_MIN, Math.min(LK_MAX, n)));
      } else {
        out[String(k)] = String(v);
      }
    }
    return out;
  });
  const [excludesByPlayer, setExcludesByPlayer] = useState(() => {
    if (wantNewSetupFromUrl) return {};
    const o = settings?.excludesByPlayer;
    if (!o || typeof o !== "object") return {};
    const out = {};
    for (const [k, arr] of Object.entries(o)) {
      if (Array.isArray(arr))
        out[String(k)] = arr.map(Number).filter(Number.isFinite);
    }
    return out;
  });

  const [playoffVorrunden, setPlayoffVorrunden] = useState(() => {
    if (wantNewSetupFromUrl) return 3;
    const v = Number(
      settings?.playoffVorrunden ??
        settings?.playoffSettings?.vorrundenCount ??
        3,
    );
    return Number.isFinite(v) && v >= 1 ? Math.min(99, Math.trunc(v)) : 3;
  });
  const [playoffKoTeilnehmer, setPlayoffKoTeilnehmer] = useState(() => {
    if (wantNewSetupFromUrl) return 4;
    const v = Number(
      settings?.playoffKoTeilnehmer ??
        settings?.playoffSettings?.koTeilnehmer ??
        4,
    );
    return [2, 4, 6, 8].includes(v) ? v : 4;
  });
  const [playThirdPlace, setPlayThirdPlace] = useState(() => {
    if (wantNewSetupFromUrl) return false;
    return settings?.playThirdPlace === true;
  });

  // Bei Korrektur: Nachladen aus DB aktualisiert state nach Mount – rrMode/Playoffs einmal aus State nachziehen
  const syncedFromStateRef = useRef(false);
  useEffect(() => {
    if (wantNewSetupFromUrl) {
      syncedFromStateRef.current = false;
      return;
    }
    if (syncedFromStateRef.current) return;
    const fromState = __asStr(
      mod?.resultRules?.mode ||
        tournament?.settings?.rrMode ||
        meta?.settings?.rrMode ||
        meta?.rrMode ||
        "",
    );
    if (fromState) {
      setRrMode(fromState);
      const vorr = Number(
        settings?.playoffVorrunden ??
          settings?.playoffSettings?.vorrundenCount ??
          0,
      );
      if (vorr >= 1 && vorr <= 99) setPlayoffVorrunden(Math.trunc(vorr));
      const ko = Number(
        settings?.playoffKoTeilnehmer ??
          settings?.playoffSettings?.koTeilnehmer ??
          0,
      );
      if ([2, 4, 6, 8].includes(ko)) setPlayoffKoTeilnehmer(ko);
      if (settings?.playThirdPlace === true) setPlayThirdPlace(true);
      syncedFromStateRef.current = true;
    }
  }, [
    wantNewSetupFromUrl,
    mod?.resultRules?.mode,
    tournament?.settings?.rrMode,
    meta?.settings?.rrMode,
    meta?.rrMode,
    settings?.playoffVorrunden,
    settings?.playoffSettings?.vorrundenCount,
    settings?.playoffKoTeilnehmer,
    settings?.playoffSettings?.koTeilnehmer,
    settings?.playThirdPlace,
  ]);

  const defaultPeriods = () => ({
    overall: { startDate: "", endDate: "" },
    groups: { startDate: "", endDate: "" },
    ko: { startDate: "", endDate: "" },
  });
  const [periods, setPeriods] = useState(() => {
    if (wantNewSetupFromUrl) return defaultPeriods();
    const p = meta?.periods;
    if (!p || typeof p !== "object") return defaultPeriods();
    return {
      overall: {
        startDate: __asStr(p.overall?.startDate ?? ""),
        endDate: __asStr(p.overall?.endDate ?? ""),
      },
      groups: {
        startDate: __asStr(p.groups?.startDate ?? ""),
        endDate: __asStr(p.groups?.endDate ?? ""),
      },
      ko: {
        startDate: __asStr(p.ko?.startDate ?? ""),
        endDate: __asStr(p.ko?.endDate ?? ""),
      },
    };
  });

  const extPlayerNamesJson = useMemo(() => {
    const ext = state?.shared?.players?.names || mod?.players?.names;
    return JSON.stringify(ext && typeof ext === "object" ? ext : {});
  }, [state?.shared?.players?.names, mod?.players?.names]);

  // Nach Remount/Adapter-Sync kann shared.players.names mehr Einträge haben als der Hook-Snapshot von `names`.
  // Nur anreichern wenn der zentrale State streng mehr ausgefüllte Namen hat (kein Überschreiben bei lokaler Reduktion).
  useEffect(() => {
    if (wantNewSetupFromUrl) return;
    let ext;
    try {
      ext = JSON.parse(extPlayerNamesJson || "{}");
    } catch {
      return;
    }
    if (!ext || typeof ext !== "object") return;
    setNames((prev) => {
      const nExt = __countFilledNames(ext);
      const nCur = __countFilledNames(prev);
      if (nExt > nCur) return { ...ext };
      return prev;
    });
  }, [wantNewSetupFromUrl, extPlayerNamesJson, setNames]);

  useEffect(() => {
    if (format !== "fixedDoubles" || teamAllocationMode !== "lkBased") return;
    const result = __sortPlayersByLk(
      names,
      lkByPlayer,
      excludesByPlayer,
      manualTeams,
    );
    if (!result.orderChanged) return;
    setNames(result.newNames);
    setLkByPlayer(result.newLkByPlayer);
    setExcludesByPlayer(result.newExcludesByPlayer);
    setManualTeams(result.newManualTeams);
  }, [format, teamAllocationMode, lkByPlayer]);

  /* MatchMixer (Schleifchen): Playoffs nur bei >7 Spielern; bei rotierendem Doppel ohne Wechsel zu Feste Teams unzulässig */
  useEffect(() => {
    if (moduleKey !== "schleifchen") return;
    if (rrMode !== "TWO_SETS_MTB") return;
    const pc = getValidPlayersCount;
    if (!shouldClearSchleifchenPlayoffsMode({ playerCount: pc, format }))
      return;
    setRrMode("ONE_SET_OPEN");
  }, [moduleKey, rrMode, format, getValidPlayersCount, setRrMode]);

  const [wizardMode, setWizardMode] = useState(() => {
    try {
      const v = String(localStorage.getItem(storageKey) || "").trim();
      return v === "expert" ? "expert" : "simple";
    } catch (_) {
      return "simple";
    }
  });

  const venueSettingsSrc =
    settings?.venue ?? meta?.settings?.venue ?? meta?.venue;
  const audienceSettingsSrc =
    settings?.audience ?? meta?.settings?.audience ?? meta?.audience;

  const [venue, setVenue] = useState(() => {
    if (wantNewSetupFromUrl) return defaultTournamentVenue();
    return normalizeTournamentVenue(venueSettingsSrc);
  });

  const [audience, setAudience] = useState(() => {
    if (wantNewSetupFromUrl) return defaultTournamentAudience();
    return normalizeTournamentAudience(audienceSettingsSrc);
  });

  const registrationSettingsSrc =
    settings?.registration ?? meta?.settings?.registration;

  const [registration, setRegistration] = useState(() => {
    if (wantNewSetupFromUrl) return defaultTournamentRegistration();
    return normalizeTournamentRegistration(registrationSettingsSrc);
  });

  const participantsSettingsSrc =
    settings?.participants ?? meta?.settings?.participants;

  const [minPlayers, setMinPlayersRaw] = useState(() => {
    if (wantNewSetupFromUrl) return defaultTournamentParticipants().min;
    return normalizeTournamentParticipants(participantsSettingsSrc).min;
  });
  const [maxPlayers, setMaxPlayersRaw] = useState(() => {
    if (wantNewSetupFromUrl) return defaultTournamentParticipants().max;
    return normalizeTournamentParticipants(participantsSettingsSrc).max;
  });

  const setMinPlayers = (next) => {
    const n = Number(typeof next === "function" ? next(minPlayers) : next);
    if (!Number.isFinite(n)) return;
    const clamped = Math.max(
      TOURNAMENT_PARTICIPANTS_LIMITS.hardMin,
      Math.min(TOURNAMENT_PARTICIPANTS_LIMITS.hardMax, Math.trunc(n)),
    );
    setMinPlayersRaw(clamped);
    if (clamped > maxPlayers) {
      setMaxPlayersRaw(clamped);
    }
  };
  const setMaxPlayers = (next) => {
    const n = Number(typeof next === "function" ? next(maxPlayers) : next);
    if (!Number.isFinite(n)) return;
    const clamped = Math.max(
      TOURNAMENT_PARTICIPANTS_LIMITS.hardMin,
      Math.min(TOURNAMENT_PARTICIPANTS_LIMITS.hardMax, Math.trunc(n)),
    );
    setMaxPlayersRaw(clamped);
    if (clamped < minPlayers) {
      setMinPlayersRaw(clamped);
    }
  };

  // Persist/DB können nach dem ersten Render ankommen (Direktaufruf ?code=).
  // Dann Stammdaten einmal pro Code in die Form übernehmen.
  const stammdatenHydratedCodeRef = useRef("");
  useEffect(() => {
    if (wantNewSetupFromUrl) {
      stammdatenHydratedCodeRef.current = "";
      return;
    }
    const incomingCode = __asStr(meta?.code);
    const incomingName = __asStr(meta?.name);
    if (!incomingCode) return;
    if (stammdatenHydratedCodeRef.current === incomingCode) return;
    const venueSrc = settings?.venue ?? meta?.settings?.venue ?? meta?.venue;
    const audienceSrc =
      settings?.audience ?? meta?.settings?.audience ?? meta?.audience;
    const registrationSrc =
      settings?.registration ?? meta?.settings?.registration;
    const participantsSrc =
      settings?.participants ?? meta?.settings?.participants;
    if (
      !incomingName &&
      !venueSrc &&
      !audienceSrc &&
      !registrationSrc &&
      !participantsSrc
    ) {
      return;
    }
    stammdatenHydratedCodeRef.current = incomingCode;
    if (incomingName) {
      setTournamentName((prev) => prev || incomingName);
    }
    const rawDate = __asStr(meta?.startDate);
    const rawTime = __asStr(meta?.startTime || meta?.startsAt || "");
    if (rawDate) {
      const norm = normalizeTournamentStartIfPast(
        rawDate,
        rawTime || defaultStartClock,
        { defaultClock: defaultStartClock },
      );
      setStartDate(norm.startDate);
      if (rawTime) setStartTime(norm.startTime);
    }
    const incomingFormat = __asStr(
      tournament?.format || meta?.format || settings?.format || "",
    );
    if (
      incomingFormat === "singles" ||
      incomingFormat === "doubles" ||
      incomingFormat === "fixedDoubles"
    ) {
      setFormat(incomingFormat);
    }
    if (venueSrc) setVenue(normalizeTournamentVenue(venueSrc));
    if (audienceSrc) setAudience(normalizeTournamentAudience(audienceSrc));
    if (registrationSrc) {
      setRegistration(normalizeTournamentRegistration(registrationSrc));
    }
    if (participantsSrc) {
      const p = normalizeTournamentParticipants(participantsSrc);
      setMinPlayersRaw(p.min);
      setMaxPlayersRaw(p.max);
    }
    const mpr = Number(tournament?.minutesPerRound);
    if (Number.isFinite(mpr) && mpr >= 1) {
      setMinutesPerRound(__clampInt(mpr, 1, 60, defaultMinutesPerRound));
    }
    const tm = Number(tournament?.totalMinutes);
    if (Number.isFinite(tm) && tm >= 10) {
      setTotalMinutes(__clampInt(tm, 10, 600, defaultTotalMinutesFallback));
    }
  }, [
    wantNewSetupFromUrl,
    meta?.code,
    meta?.name,
    meta?.startDate,
    meta?.startTime,
    meta?.startsAt,
    meta?.format,
    meta?.venue,
    meta?.audience,
    meta?.settings,
    tournament?.format,
    tournament?.minutesPerRound,
    tournament?.totalMinutes,
    settings,
    defaultStartClock,
    defaultMinutesPerRound,
    defaultTotalMinutesFallback,
  ]);

  return {
    tournamentName,
    setTournamentName,
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    format,
    setFormat,
    tournamentMode,
    setTournamentMode,
    matchRules,
    setMatchRules,
    playersCount,
    setPlayersCount,
    names,
    setNames,
    getValidPlayersCount,
    minutesPerRound,
    setMinutesPerRound,
    totalMinutes,
    setTotalMinutes,
    pauseBetweenRounds,
    setPauseBetweenRounds,
    halftimeBreak,
    setHalftimeBreak,
    warmupMinutes,
    setWarmupMinutes,
    courtsCount,
    setCourtsCount,
    courtsNumbersRaw,
    setCourtsNumbersRaw,
    pointsWin,
    setPointsWin,
    pointsDraw,
    setPointsDraw,
    rrMode,
    setRrMode,
    allowBeyond7,
    setAllowBeyond7,
    expressMode,
    setExpressMode,
    alwaysAskForRest,
    setAlwaysAskForRest,
    alwaysAskForChanges,
    setAlwaysAskForChanges,
    teamAllocationMode,
    setTeamAllocationMode,
    manualTeams,
    setManualTeams,
    lkByPlayer,
    setLkByPlayer,
    excludesByPlayer,
    setExcludesByPlayer,
    playoffVorrunden,
    setPlayoffVorrunden,
    playoffKoTeilnehmer,
    setPlayoffKoTeilnehmer,
    playThirdPlace,
    setPlayThirdPlace,
    wizardMode,
    setWizardMode,
    venue,
    setVenue,
    audience,
    setAudience,
    registration,
    setRegistration,
    minPlayers,
    setMinPlayers,
    maxPlayers,
    setMaxPlayers,
    groupsKo,
    setGroupsKo,
    koOnly,
    setKoOnly,
    periods,
    setPeriods,
  };
}
